// ============================================
// RegisterPage.js — Unified Dual Theme Architecture
// Fully Responsive with Hidden Scrollbars
// ============================================

import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { registerUser, googleLogin, clearError } from '../redux/slices/authSlice';
import { auth } from '../config/firebase';
import { RecaptchaVerifier, signInWithPhoneNumber, signInWithPopup, GoogleAuthProvider } from 'firebase/auth';
import { useTheme } from '../context/ThemeContext';
import api from '../services/api';

const RegisterPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated, isLoading, error, user } = useSelector((state) => state.auth);
    const { theme, toggleTheme } = useTheme();

    const queryParams = new URLSearchParams(location.search);
    const queryRole = queryParams.get('role');
    const initialRole = queryRole === 'pa' ? 'pa'
        : (queryRole === 'driver' || location.pathname.includes('driver')) ? 'driver'
        : (localStorage.getItem('lastRole') === 'pa' ? 'pa' : 'driver');

    const [role, setRole] = useState(initialRole);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        phone: '',
        password: '',
    });
    const [showPassword, setShowPassword] = useState(false);

    const [step, setStep] = useState(1);
    const [confirmationResult, setConfirmationResult] = useState(null);
    const [otp, setOtp] = useState('');
    const [authLoading, setAuthLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');

    useEffect(() => {
        if (isAuthenticated && user) {
            const routes = { admin: '/admin', dispatcher: '/dispatcher', driver: '/driver', user: '/user', pa: '/pa' };
            navigate(routes[user.role] || '/login');
        }
    }, [isAuthenticated, user, navigate]);

    useEffect(() => () => dispatch(clearError()), [dispatch]);

    const isDriver = role === 'driver';
    const isPA = role === 'pa';

    const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

    useEffect(() => {
        // Pre-initialize and pre-render Recaptcha to significantly speed up OTP sending
        if (!window.recaptchaVerifier) {
            try {
                window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', {
                    size: 'invisible',
                });
                window.recaptchaVerifier.render().catch(console.error);
            } catch (e) {
                console.error('Recaptcha init error', e);
            }
        }

        return () => {
            if (window.recaptchaVerifier) {
                try {
                    window.recaptchaVerifier.clear();
                } catch (e) {
                    console.log('Recaptcha clear error', e);
                }
                window.recaptchaVerifier = null;
            }
        };
    }, []);

    const handleSubmitStep1 = async (e) => {
        e.preventDefault();
        
        if (!formData.name.trim()) return setFormError('Please enter your full name.');
        if (!formData.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) return setFormError('Please enter a valid email address.');
        if (!formData.phone.trim()) return setFormError('Please enter your phone number.');
        if (!formData.password) return setFormError('Please create a password.');

        // Client-side password strength check
        const pwd = formData.password;
        if (pwd.length < 8 || !/[A-Z]/.test(pwd) || !/[a-z]/.test(pwd) || !/[0-9]/.test(pwd) || !/[!@#$%^&*(),.?":{}|<>]/.test(pwd)) {
            setFormError('Password must be at least 8 characters with uppercase, lowercase, number, and special character.');
            return;
        }
        setFormError('');
        setFormSuccess('');
        setAuthLoading(true);

        try {
            // ── Check if email exists BEFORE doing ReCaptcha/OTP ──
            try {
                await api.post('/auth/check-email', { email: formData.email, role });
            } catch (apiError) {
                const message = apiError.response?.data?.message || 'Error checking email availability';
                setFormError(message);
                setAuthLoading(false);
                return; // Stop here if email exists
            }

            if (!window.recaptchaVerifier) {
                window.recaptchaVerifier = new RecaptchaVerifier(auth, 'recaptcha-container', { size: 'invisible' });
                await window.recaptchaVerifier.render();
            }
            const appVerifier = window.recaptchaVerifier;

            // ── E.164 Phone Normalization — handles ALL UK/PK formats ──
            // Firebase requires E.164: +[countryCode][subscriberNumber]
            // Examples: +447911123456, +923065550452
            let raw = formData.phone.trim();
            let digits = raw.replace(/[^\d]/g, ''); // strip ALL non-digits

            // Step 1: Remove international dial prefix (00)
            // 0044... → 44..., 0092... → 92...
            if (digits.startsWith('00')) digits = digits.slice(2);

            // Step 2: Remove redundant 0 after country code
            // +44 07... → 4407... → 44 + 7..., +92 03... → 9203... → 92 + 3...
            if (digits.startsWith('440')) digits = '44' + digits.slice(3);
            if (digits.startsWith('920')) digits = '92' + digits.slice(3);

            // Step 3: Local format → prepend country code
            if (digits.startsWith('07') || digits.startsWith('01') || digits.startsWith('02') || digits.startsWith('08')) {
                // UK local number (07xxx, 01xxx, 02xxx, 08xxx)
                digits = '44' + digits.slice(1);
            } else if (digits.startsWith('03') && digits.length === 11) {
                // PK local number (03xxx, 11 digits)
                digits = '92' + digits.slice(1);
            } else if (digits.startsWith('0')) {
                // Any other 0-prefix → assume UK
                digits = '44' + digits.slice(1);
            }

            // Step 4: Bare subscriber number (no country code)
            // UK subscriber: 7xxx (10 digits), PK subscriber: 3xxx (10 digits)
            if (!digits.startsWith('44') && !digits.startsWith('92') && !digits.startsWith('1') && !digits.startsWith('33') && !digits.startsWith('49') && !digits.startsWith('91')) {
                if (digits.startsWith('7') || digits.startsWith('1') || digits.startsWith('2') || digits.startsWith('8')) {
                    digits = '44' + digits; // UK
                } else if (digits.startsWith('3') && digits.length === 10) {
                    digits = '92' + digits; // PK
                } else {
                    digits = '44' + digits; // default UK
                }
            }

            const phone = '+' + digits;
            console.log('[PhoneAuth] Normalized:', raw, '→', phone);

            const confirmation = await signInWithPhoneNumber(auth, phone, appVerifier);
            setConfirmationResult(confirmation);
            setStep(2);
        } catch (err) {
            // ── User-friendly Firebase error messages ──
            const code = err.code || '';
            const friendlyErrors = {
                'auth/captcha-check-failed': 'Verification failed. Please refresh the page and try again.',
                'auth/invalid-phone-number': 'Invalid phone number. Please enter a valid number with country code (e.g. +44 7911123456).',
                'auth/too-many-requests': 'Too many attempts. Please wait a few minutes and try again.',
                'auth/quota-exceeded': 'SMS quota exceeded. Please try again later.',
                'auth/network-request-failed': 'Network error. Please check your internet connection.',
                'auth/missing-phone-number': 'Please enter a phone number.',
                'auth/user-disabled': 'This account has been disabled. Please contact support.',
            };
            setFormError(friendlyErrors[code] || 'Phone verification failed. Please check your number and try again.');

            // Reset recaptcha so user can retry
            if (window.recaptchaVerifier) {
                try { window.recaptchaVerifier.clear(); } catch(e) {}
                window.recaptchaVerifier = null;
            }
        } finally {
            setAuthLoading(false);
        }
    };

    const handleSubmitStep2 = async (e) => {
        e.preventDefault();
        setFormError('');
        
        if (!otp || otp.trim().length !== 6) {
            return setFormError('Please enter the 6-digit verification code.');
        }

        setAuthLoading(true);
        try {
            const result = await confirmationResult.confirm(otp);
            const idToken = await result.user.getIdToken();
            await dispatch(registerUser({ ...formData, role, firebaseToken: idToken })).unwrap();

            setFormSuccess('Account created successfully! Redirecting to login...');
            setTimeout(() => navigate('/login'), 2000);
        } catch (err) {
            // Show the actual backend error (e.g., "User with this email already exists")
            const msg = typeof err === 'string' ? err : (err?.message || err?.data?.message || 'Registration failed. Please try again.');
            setFormError(msg);
        } finally {
            setAuthLoading(false);
        }
    };

    const handleGoogleSignIn = async () => {
        const provider = new GoogleAuthProvider();
        try {
            setFormError('');
            const result = await signInWithPopup(auth, provider);
            const idToken = await result.user.getIdToken();
            await dispatch(googleLogin({ firebaseToken: idToken, role })).unwrap();
            
            // Note: If successful, the auth state change will typically trigger a redirect automatically
            // depending on how your protected routes are set up.
        } catch (err) {
            console.error("Google Sign-in failed", err);
            const msg = typeof err === 'string' ? err : (err?.message || 'Google Sign-In failed. Please try again.');
            // Filter out user-cancelled popup errors which are normal
            if (!msg.includes('auth/popup-closed-by-user')) {
                setFormError(msg);
            }
        }
    };

    return (
        <>
            <style>{`
                .grecaptcha-badge { visibility: hidden !important; }
            `}</style>
            <div className="fixed inset-0 flex bg-slate-50 dark:bg-[#050510] overflow-hidden font-['Inter',sans-serif] transition-colors duration-500">
                {/* ── Left Panel: Cinematic Image Overlay ───────── */}
            <div className={`hidden lg:flex lg:w-[40%] xl:w-[45%] relative group overflow-hidden`}>
                <div className="absolute inset-0 bg-slate-900 dark:bg-black transition-colors duration-500">
                    <img
                        src="/assets/login-bg.png"
                        alt="IdaCab Registration"
                        className="w-full h-full object-cover opacity-[0.85] dark:opacity-40 grayscale-[10%] group-hover:scale-105 transition-transform duration-[20s] ease-out mix-blend-overlay"
                    />
                    <div className="absolute inset-0 bg-gradient-to-r from-black/70 to-transparent transition-colors duration-500" />
                </div>

                <div className="relative z-10 flex flex-col justify-between p-16 w-full h-full border-r border-slate-200 dark:border-white/5 transition-colors">
                    <div className="flex items-start">
                        <div className="bg-white/95 p-1.5 rounded-2xl shadow-[0_8px_30px_rgb(0,0,0,0.12)] inline-flex items-center justify-center backdrop-blur-sm">
                            <img src="/assets/logo.png" alt="IdaCab Logo" className="h-16 sm:h-24 w-auto object-contain" />
                        </div>
                    </div>

                    <div className="max-w-lg mb-12 transform group-hover:translate-x-2 transition-transform duration-700 ease-out">
                        <h2 className="text-4xl lg:text-5xl font-black text-white mb-8 tracking-tight leading-tight drop-shadow-2xl">
                            Start Your
                            <span className="block text-teal-400">Journey with Us</span>
                        </h2>

                        <ul className="space-y-6">
                            {(isPA
                            ? [
                                { t: 'Secure Portal', d: 'Upload compliance documents safely.' },
                                { t: 'Quick Verification', d: 'Get approved and start working fast.' }
                            ]
                            : isDriver
                                ? [
                                    { t: 'High Earnings', d: 'Get elite rates for London school & private contracts.' },
                                    { t: 'Compliance First', d: 'UK leading safety and document management.' }
                                ]
                                : [
                                    { t: 'Seamless Booking', d: 'Book elite transport in under 60 seconds.' },
                                    { t: 'Safety Links', d: 'Verified drivers and live GPS monitoring.' }
                                ]
                            ).map((item, i) => (
                                <li key={i} className="flex gap-4">
                                    <div className="w-5 h-5 rounded-full bg-white/10 flex-shrink-0 flex items-center justify-center text-[10px] font-black text-white border border-white/20">
                                        {i + 1}
                                    </div>
                                    <div>
                                        <p className="text-sm font-black text-white tracking-wide uppercase drop-shadow-md">{item.t}</p>
                                        <p className="text-xs text-white/70 font-medium leading-relaxed max-w-[200px]">{item.d}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="flex items-center gap-6 text-white/50 text-[11px] font-black tracking-widest uppercase">
                        Elite Passenger Services
                    </div>
                </div>
            </div>

            {/* ── Right Panel: Form (Scrollbar Hidden) ────────── */}
            <div className="flex-1 min-w-0 h-full relative selection:bg-teal-500/20 z-10 flex flex-col justify-center items-center p-4 sm:p-6 lg:p-8 overflow-hidden">

                {/* Mobile Background Overlay (Hidden on desktop) */}
                <div className="absolute inset-0 lg:hidden z-0">
                    <img src="/assets/login-bg.png" alt="Background" className="w-full h-full object-cover opacity-30 dark:opacity-20 grayscale-[20%]" />
                    <div className="absolute inset-0 bg-gradient-to-b from-slate-50/80 via-slate-50/90 to-slate-50 dark:from-[#050510]/80 dark:via-[#050510]/90 dark:to-[#050510] backdrop-blur-sm" />
                </div>

                {/* Global Theme Toggle */}
                <div className="absolute top-6 right-6 z-50">
                    <button
                        onClick={toggleTheme}
                        className="p-3 rounded-full bg-white/50 dark:bg-slate-800/50 backdrop-blur-xl text-slate-500 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white transition-all border border-slate-200 dark:border-slate-700 hover:scale-105 active:scale-95 shadow-sm"
                        aria-label="Toggle theme"
                    >
                        {theme === 'light' ? (
                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                            </svg>
                        ) : (
                            <svg className="w-5 h-5 text-amber-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                            </svg>
                        )}
                    </button>
                </div>

                {/* Main Glass Form Container */}
                <div className="w-full max-w-[460px] min-w-0 max-h-[92vh] sm:max-h-[85vh] overflow-y-auto overflow-x-hidden scrollbar-hide bg-white/80 dark:bg-slate-900/60 backdrop-blur-3xl rounded-[2.5rem] p-6 sm:p-8 shadow-2xl shadow-slate-200 dark:shadow-black/50 border border-white/60 dark:border-white/5 transition-colors duration-500 relative z-10">

                    <div className="mb-6 flex flex-col items-center lg:hidden">
                        <div className="bg-white/95 p-1.5 rounded-2xl shadow-lg inline-flex items-center justify-center">
                            <img src="/assets/logo.png" alt="IdaCab Logo" className="h-16 w-auto object-contain" />
                        </div>
                    </div>

                    <div className="text-center mb-6">
                        <h2 className="text-3xl font-black text-slate-800 dark:text-white tracking-tight mb-2 transition-colors">Create Account</h2>
                        <p className="text-slate-500 dark:text-slate-400 text-sm font-bold transition-colors">Join the IdaCab elite transport network.</p>
                    </div>

                    {/* Role Toggle: Driver / PA Portal */}
                    <div className="flex gap-2 p-1 mb-6 bg-slate-50 dark:bg-slate-950/50 rounded-xl border border-slate-200 dark:border-slate-800 backdrop-blur-sm transition-colors">
                        <button
                            onClick={() => { setRole('driver'); localStorage.setItem('lastRole', 'driver'); }}
                            className={`flex-1 py-3.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all duration-300 relative ${isDriver ? 'text-white shadow-md bg-slate-900 dark:bg-slate-800' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`}
                        >
                            <span className="relative z-10">Driver Portal</span>
                        </button>
                        <button
                            onClick={() => { setRole('pa'); localStorage.setItem('lastRole', 'pa'); }}
                            className={`flex-1 py-3.5 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all duration-300 relative ${isPA ? 'text-white shadow-md bg-slate-900 dark:bg-slate-800' : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'}`}
                        >
                            <span className="relative z-10">PA Portal</span>
                        </button>
                    </div>

                    {/* Unified Error Banner */}
                    {(error || formError) && (
                        <div className="mb-6 flex items-start gap-3 px-4 py-4 rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="w-8 h-8 rounded-xl bg-rose-100 dark:bg-rose-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <svg className="w-4 h-4 text-rose-600 dark:text-rose-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-rose-700 dark:text-rose-300">Registration Failed</p>
                                <p className="text-[13px] text-rose-600 dark:text-rose-400 mt-0.5 leading-relaxed">
                                    {formError || error}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Success Banner */}
                    {formSuccess && (
                        <div className="mb-6 flex items-start gap-3 px-4 py-4 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 animate-in fade-in slide-in-from-top-2 duration-300">
                            <div className="w-8 h-8 rounded-xl bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                                <svg className="w-4 h-4 text-emerald-600 dark:text-emerald-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-bold text-emerald-700 dark:text-emerald-300">Success</p>
                                <p className="text-[13px] text-emerald-600 dark:text-emerald-400 mt-0.5 leading-relaxed">
                                    {formSuccess}
                                </p>
                            </div>
                        </div>
                    )}

                    {step === 1 ? (
                        <form onSubmit={handleSubmitStep1} noValidate className="space-y-4 relative z-10">
                            <div className="space-y-1.5">
                                <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-1">Full Name</label>
                                <input name="name" type="text" required value={formData.name} onChange={handleChange}
                                    className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all text-sm font-bold shadow-sm hover:border-slate-300 dark:hover:border-slate-600"
                                    placeholder="Enter your full name" />
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-1">Email</label>
                                    <input name="email" type="email" required value={formData.email} onChange={handleChange}
                                        className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all text-sm font-bold shadow-sm hover:border-slate-300 dark:hover:border-slate-600"
                                        placeholder="name@email.com" />
                                </div>
                                <div className="space-y-1.5">
                                    <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-1">Phone</label>
                                    <input name="phone" type="tel" required value={formData.phone} onChange={handleChange}
                                        className="w-full px-5 py-3.5 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all text-sm font-bold shadow-sm hover:border-slate-300 dark:hover:border-slate-600"
                                        placeholder="+44 7000..." />
                                </div>
                            </div>

                            <div className="space-y-1.5">
                                <label className="block text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest pl-1">Password</label>
                                <div className="relative">
                                    <input name="password" type={showPassword ? 'text' : 'password'} required value={formData.password} onChange={handleChange}
                                        className="w-full px-5 py-3.5 pr-12 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all text-sm font-black tracking-widest shadow-sm hover:border-slate-300 dark:hover:border-slate-600"
                                        placeholder="••••••••" />
                                    <button type="button" onClick={() => setShowPassword(!showPassword)}
                                        className="absolute inset-y-0 right-0 pr-4 flex items-center text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition-colors">
                                        {showPassword ? (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" /></svg>
                                        ) : (
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                                        )}
                                    </button>
                                </div>

                                {/* Password Strength Indicator */}
                                {formData.password.length > 0 && (
                                    <div className="mt-2 p-3 rounded-xl bg-slate-50 dark:bg-slate-800/30 border border-slate-200 dark:border-slate-700/50 transition-colors">
                                        <div className="grid grid-cols-2 gap-1.5">
                                            {[
                                                { test: formData.password.length >= 8, label: '8+ characters' },
                                                { test: /[A-Z]/.test(formData.password), label: 'Uppercase (A-Z)' },
                                                { test: /[a-z]/.test(formData.password), label: 'Lowercase (a-z)' },
                                                { test: /[0-9]/.test(formData.password), label: 'Number (0-9)' },
                                                { test: /[!@#$%^&*(),.?":{}|<>]/.test(formData.password), label: 'Symbol (!@#$%)' },
                                            ].map((rule, i) => (
                                                <div key={i} className={`flex items-center gap-1.5 text-[10px] font-bold transition-colors ${rule.test ? 'text-emerald-600 dark:text-emerald-400' : 'text-slate-400 dark:text-slate-500'}`}>
                                                    {rule.test ? (
                                                        <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                                                    ) : (
                                                        <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" /></svg>
                                                    )}
                                                    {rule.label}
                                                </div>
                                            ))}
                                        </div>
                                        {/* Strength Bar */}
                                        <div className="mt-2 h-1 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden">
                                            <div
                                                className={`h-full rounded-full transition-all duration-300 ${[formData.password.length >= 8, /[A-Z]/.test(formData.password), /[a-z]/.test(formData.password), /[0-9]/.test(formData.password), /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)].filter(Boolean).length <= 2
                                                        ? 'bg-red-500 w-[20%]'
                                                        : [formData.password.length >= 8, /[A-Z]/.test(formData.password), /[a-z]/.test(formData.password), /[0-9]/.test(formData.password), /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)].filter(Boolean).length <= 3
                                                            ? 'bg-amber-500 w-[50%]'
                                                            : [formData.password.length >= 8, /[A-Z]/.test(formData.password), /[a-z]/.test(formData.password), /[0-9]/.test(formData.password), /[!@#$%^&*(),.?":{}|<>]/.test(formData.password)].filter(Boolean).length <= 4
                                                                ? 'bg-teal-500 w-[75%]'
                                                                : 'bg-emerald-500 w-full'
                                                    }`}
                                            />
                                        </div>
                                    </div>
                                )}
                            </div>

                            {isDriver && (
                                <div className="bg-slate-50 dark:bg-slate-800/40 border border-slate-200 dark:border-slate-700/50 p-4 rounded-2xl text-[10px] text-slate-500 dark:text-slate-400 font-bold leading-relaxed border-l-4 border-l-teal-500 transition-colors">
                                    * Vehicle and insurance documents will be required after verification.
                                </div>
                            )}

                            <div id="recaptcha-container" className="hidden"></div>

                            <button type="submit" disabled={isLoading || authLoading}
                                className="w-full py-4 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-[11px] font-black uppercase tracking-[0.2em] shadow-lg shadow-teal-500/20 active:scale-[0.98] disabled:opacity-50 disabled:grayscale transition-all flex justify-center items-center h-[52px] relative overflow-hidden"
                            >
                                {isLoading || authLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <span className="relative z-10">Verify Device</span>}
                            </button>

                            <p className="text-[9px] text-slate-400 dark:text-slate-500 text-center leading-relaxed px-4">
                                This site is protected by reCAPTCHA and the Google <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer" className="underline hover:text-teal-500 transition-colors">Privacy Policy</a> and <a href="https://policies.google.com/terms" target="_blank" rel="noreferrer" className="underline hover:text-teal-500 transition-colors">Terms of Service</a> apply.
                            </p>

                            <div className="relative flex items-center py-2">
                                <div className="flex-grow border-t border-slate-200 dark:border-slate-800 transition-colors"></div>
                                <span className="flex-shrink-0 mx-4 text-[9px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest">or continue with</span>
                                <div className="flex-grow border-t border-slate-200 dark:border-slate-800 transition-colors"></div>
                            </div>

                            <button type="button" onClick={handleGoogleSignIn} disabled={isLoading || authLoading}
                                className="w-full flex items-center justify-center gap-3 py-4 rounded-2xl bg-white dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800 lg:hover:-translate-y-0.5 text-slate-700 dark:text-slate-200 text-[10px] font-black uppercase tracking-[0.15em] transition-all disabled:opacity-50 shadow-sm"
                            >
                                <svg viewBox="0 0 24 24" className="w-5 h-5 drop-shadow-sm" xmlns="http://www.w3.org/2000/svg"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" /><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" /><path d="M1 1h22v22H1z" fill="none" /></svg>
                                Sign Up via Google
                            </button>
                        </form>
                    ) : (
                        <form onSubmit={handleSubmitStep2} noValidate className="space-y-6 relative z-10">
                            <div className="text-center mb-8">
                                <h3 className="text-2xl font-black text-slate-900 dark:text-white mb-2 transition-colors">OTP Verification</h3>
                                <p className="text-sm text-slate-500 dark:text-slate-400 font-medium">
                                    We sent a secure code to <span className="font-bold text-slate-800 dark:text-white">{formData.phone}</span>
                                </p>
                            </div>

                            <div className="space-y-2 uppercase tracking-[0.15em] text-[10px] font-black text-slate-500 dark:text-slate-400 text-center">
                                <label>6-Digit Code</label>
                                <input type="text" required value={otp} onChange={(e) => setOtp(e.target.value)}
                                    maxLength={6}
                                    className="w-full text-center tracking-[0.5em] text-2xl font-bold px-5 py-6 rounded-2xl bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700 text-slate-900 dark:text-white focus:border-teal-500 focus:bg-white dark:focus:bg-slate-800 focus:ring-4 focus:ring-teal-500/10 outline-none transition-all shadow-sm hover:border-slate-300 dark:hover:border-slate-600"
                                    placeholder="••••••" />
                            </div>

                            <button type="submit" disabled={isLoading || authLoading}
                                className="w-full py-4 mt-2 rounded-2xl bg-gradient-to-r from-teal-500 to-cyan-600 hover:from-teal-600 hover:to-cyan-700 text-white text-[11px] font-black uppercase tracking-[0.2em] shadow-lg shadow-teal-500/20 active:scale-[0.98] disabled:opacity-50 disabled:grayscale transition-all flex justify-center items-center h-[56px] relative overflow-hidden"
                            >
                                {isLoading || authLoading ? <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div> : <span className="relative z-10">Verify & Link Account</span>}
                            </button>

                            <div className="text-center mt-6">
                                <button
                                    type="button"
                                    onClick={() => {
                                        setStep(1);
                                        setOtp('');
                                    }}
                                    disabled={isLoading || authLoading}
                                    className="text-[10px] font-black text-slate-400 dark:text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 uppercase tracking-widest transition-colors disabled:opacity-50"
                                >
                                    Didn't get code? Resend
                                </button>
                            </div>
                        </form>
                    )}

                    <div className="mt-6 text-center pt-2">
                        <p className="text-slate-500 dark:text-slate-400 text-[11px] font-bold tracking-wide uppercase transition-colors">
                            Already part of IdaCab? <Link to="/login" className="text-teal-600 dark:text-teal-400 font-black hover:text-teal-700 dark:hover:text-teal-300 ml-1 underline-offset-4 hover:underline transition-colors mt-2 sm:mt-0 inline-block">Sign In</Link>
                        </p>
                    </div>
                </div>
            </div>
        </div>
        </>
    );
};

export default RegisterPage;
